// Client for per-project phase schedules (Gantt Phase Schedule Slice 1).
// source / verified / updatedBy / updatedAt are server-owned: never sent.
import { NetworkError, ValidationError, errorFromResponse } from "./errors.js";
import { invalidateCache } from "./projectsClient.js";

const BASE = "/api/projects";
const BATCH = 150; // keep query strings bounded; chunks run in parallel (no waterfall)

async function apiFetch(url, options = {}) {
  let res;
  try {
    res = await fetch(url, { credentials: "include", ...options });
  } catch (err) {
    throw new NetworkError(`Network error: ${err.message}`);
  }
  if (!res.ok) throw await errorFromResponse(res);
  return res.json();
}

export async function getProjectSchedule(id) {
  if (!id || typeof id !== "string") throw new ValidationError("getProjectSchedule: id required");
  return apiFetch(`${BASE}/${encodeURIComponent(id)}/schedule`);
}

export async function saveProjectSchedule(id, body) {
  if (!id || typeof id !== "string") throw new ValidationError("saveProjectSchedule: id required");
  if (!body || !Array.isArray(body.phases)) throw new ValidationError("saveProjectSchedule: phases required");
  const saved = await apiFetch(`${BASE}/${encodeURIComponent(id)}/schedule`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  invalidateCache();
  return saved;
}

export async function getProjectSchedules(ids) {
  const list = [...new Set((ids || []).filter((v) => typeof v === "string" && v))];
  if (list.length === 0) return { schedules: [] };
  const chunks = [];
  for (let i = 0; i < list.length; i += BATCH) chunks.push(list.slice(i, i + BATCH));
  const results = await Promise.all(chunks.map((c) =>
    apiFetch(`${BASE}/phase-schedules?projectIds=${c.map(encodeURIComponent).join(",")}`)));
  if (results.some(r => !Array.isArray(r?.schedules)))
    throw new ValidationError("The server returned an invalid schedule list.");
  return { schedules: results.flatMap(r => r.schedules) };
}

/** Window event fired after a schedule save so mounted portfolio Gantts refetch. */
export const SCHEDULE_SAVED_EVENT = "projects:schedule-saved";
